import type { UseFetchOptions } from 'nuxt/app'
import type { ComputedRef } from 'vue'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'

export interface StrapiConfig {
  baseUrl: string
  headers: Record<string, string>
  isPreview: boolean
}

export const useStrapiConfig = (): StrapiConfig => {
  const config = useRuntimeConfig()
  const preview = useRoute().query.preview === 'true'
  const { strapiApiBaseURL, strapiDraftBaseURL, strapiApiKey } = config.public
  return {
    baseUrl: (preview ? strapiDraftBaseURL : strapiApiBaseURL) as string,
    headers: { Authorization: `Bearer ${strapiApiKey}` },
    isPreview: preview,
  }
}

export const buildStrapiUrl = (
  config: StrapiConfig,
  urlSuffix: string
): string => {
  const url = new URL(config.baseUrl + urlSuffix.replace(/^\/?(api\/)?/, ''))
  if (config.isPreview) url.searchParams.set('status', 'draft')
  return url.href
}

export function useStrapi<T>({
  urlSuffix,
  options = {},
  lazy = false,
}: {
  urlSuffix: Ref<string> | ComputedRef<string> | string
  options?: UseFetchOptions<StrapiApiDefaultResponse<T[]>, T[]>
  lazy?: boolean
}) {
  const strapiConfig = useStrapiConfig()
  const href = buildStrapiUrl(strapiConfig, unref(urlSuffix))
  const urlRef = new URL(href)

  let key = urlRef.pathname + urlRef.search
  if (key[0] === '/') {
    key = key.slice(1)
  }

  const defaults: UseFetchOptions<StrapiApiDefaultResponse<T[]>, T[]> = {
    baseURL: urlRef.origin,
    key,
    headers: strapiConfig.headers,
    transform: (data) => data.data,
  }

  const params = { ...defaults, ...options }

  return lazy ? useLazyFetch(href, params) : useFetch(href, params)
}
